import React, { useRef, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { DialogoEnlace } from './ui/enlace';
import { escaparHtml } from '../lib/htmlSeguro';
import { Bold, Italic, Underline, Link2, Image as ImageIcon, Loader2, Eraser } from 'lucide-react';

const SIZES = [{ l: 'Pequeño', v: '2' }, { l: 'Normal', v: '3' }, { l: 'Grande', v: '5' }, { l: 'Enorme', v: '6' }];

export default function SignatureEditor({ value, onChange }) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [selectedImg, setSelectedImg] = useState(null);
  const [imgWidth, setImgWidth] = useState(160);

  useEffect(() => { if (ref.current && ref.current.innerHTML !== (value || '')) ref.current.innerHTML = value || ''; }, []);

  const emit = () => onChange(ref.current?.innerHTML || '');
  const exec = (cmd, val = null) => { ref.current?.focus(); document.execCommand(cmd, false, val); emit(); };
  const [enlace, setEnlace] = useState(null);   // diálogo propio en vez de window.prompt (M27)
  const addLink = () => {
    const sel = window.getSelection();
    const rango = sel && sel.rangeCount && ref.current?.contains(sel.anchorNode) ? sel.getRangeAt(0).cloneRange() : null;
    setEnlace({ rango });
  };
  const ponerEnlace = (url) => {
    const ed = ref.current;
    if (!ed) return;
    ed.focus();
    const sel = window.getSelection();
    if (enlace?.rango) { sel.removeAllRanges(); sel.addRange(enlace.rango); }
    // Con texto seleccionado, ese texto es el enlace; sin selección se escribe la dirección.
    if (enlace?.rango && !enlace.rango.collapsed) document.execCommand('createLink', false, url);
    else document.execCommand('insertHTML', false, `<a href="${escaparHtml(url)}">${escaparHtml(url.replace(/^mailto:/, ''))}</a>`);
    setEnlace(null);
    emit();
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post('/signature/upload-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      ref.current?.focus();
      document.execCommand('insertHTML', false, `<img src="${res.data.url}" style="width:160px;max-width:100%;height:auto;border-radius:6px" alt="firma" />`);
      emit();
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo subir la imagen'); }
    finally { setUploading(false); }
  };

  const onEditorClick = (e) => {
    if (e.target.tagName === 'IMG') {
      setSelectedImg(e.target);
      setImgWidth(parseInt(e.target.style.width) || e.target.clientWidth || 160);
    } else { setSelectedImg(null); }
  };

  const resizeSelected = (w) => {
    setImgWidth(w);
    if (selectedImg) { selectedImg.style.width = w + 'px'; selectedImg.style.height = 'auto'; emit(); }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center flex-wrap gap-0.5 p-1 rounded-lg border border-border bg-muted/40">
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => exec('bold')} aria-label="Negrita" data-testid="sig-bold"><Bold className="h-4 w-4" /></Button>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => exec('italic')} aria-label="Cursiva"><Italic className="h-4 w-4" /></Button>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => exec('underline')} aria-label="Subrayado"><Underline className="h-4 w-4" /></Button>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={addLink} aria-label="Enlace"><Link2 className="h-4 w-4" /></Button>
        <select onChange={(e) => exec('fontSize', e.target.value)} defaultValue="3" className="h-8 rounded-md bg-transparent text-sm px-1 border border-border" data-testid="sig-size" title="Tamaño de texto" aria-label="Tamaño de texto">
          {SIZES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
        <label className="h-8 w-8 grid place-items-center rounded-md hover:bg-muted cursor-pointer" title="Color de texto">
          <input type="color" onChange={(e) => exec('foreColor', e.target.value)} className="w-5 h-5 cursor-pointer border-0 bg-transparent p-0" data-testid="sig-color" aria-label="Color del texto" />
        </label>
        <Button type="button" variant="ghost" size="sm" className="h-8 gap-1.5" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="sig-image">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}Imagen
        </Button>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 ml-auto" onClick={() => exec('removeFormat')} aria-label="Limpiar formato" title="Limpiar formato"><Eraser className="h-4 w-4" /></Button>
        <input ref={fileRef} type="file" accept="image/*" className="od-file-oculto" tabIndex={-1} aria-hidden="true" onChange={onPickImage} data-testid="sig-image-input" />
      </div>

      {selectedImg && (
        <div className="flex items-center gap-3 p-2 rounded-lg bg-primary/5 border border-primary/20 text-sm" data-testid="sig-img-resize">
          <span className="text-muted-foreground shrink-0">Tamaño de imagen</span>
          <input type="range" min="40" max="500" value={imgWidth} onChange={(e) => resizeSelected(Number(e.target.value))} className="flex-1 accent-[hsl(var(--primary))]" />
          <span className="w-12 text-right tabular-nums">{imgWidth}px</span>
        </div>
      )}

      <div
        ref={ref}
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label="Firma"   // R11: el editor no tenía nombre para los lectores de pantalla
        suppressContentEditableWarning
        onInput={emit}
        onClick={onEditorClick}
        data-testid="signature-editor"
        className="min-h-[140px] max-h-[300px] overflow-auto rounded-lg border border-border px-4 py-3 outline-none text-sm leading-relaxed email-body focus:ring-2 focus:ring-primary/30"
      />

      <div>
        <div className="text-xs font-semibold text-muted-foreground mb-1.5">Previsualización (así se vera al final del correo)</div>
        <div className="rounded-lg border border-border bg-white text-[#0E1621] p-4" data-testid="signature-preview">
          <div className="text-sm text-[#0E1621]/60 mb-3 pb-3 border-b border-dashed border-gray-300">…contenido de tu correo…</div>
          <div className="text-sm leading-relaxed" style={{ fontFamily: 'system-ui, sans-serif' }} dangerouslySetInnerHTML={{ __html: value || '<span style="color:#999">Tu firma aparecerá aquí</span>' }} />
        </div>
      </div>
      <DialogoEnlace open={!!enlace} onOpenChange={(v) => !v && setEnlace(null)} onAceptar={ponerEnlace} />
    </div>
  );
}
